import request from '@/utils/request'

/**
 * 获取告警列表
 * @param {Object} data - 请求参数
 */
export function getAlarmList(data) {
  return request({
    url: '/supervisor/alarm/list',
    method: 'get',
    params: data
  })
}

/**
 * 获取告警类型统计
 * @param {Object} params - 请求参数
 * @param {string} params.startTime - 开始时间
 * @param {string} params.endTime - 结束时间
 */
export function getAlarmTypeCount(params) {
  return request({
    url: '/supervisor/alarm/type/count',
    method: 'get',
    params
  })
}

/**
 * 获取电站告警统计
 * @param {Object} params - 请求参数
 * @param {string} params.startTime - 开始时间
 * @param {string} params.endTime - 结束时间
 */
export function getAlarmStationCount(params) {
  return request({
    url: '/supervisor/alarm/station/count',
    method: 'get',
    params
  })
}




//#region 告警配置
//////////////////////////////////////////////////////////////

/**
 * @typedef {Object} AlarmCodeList 
 * @property {string} alarmCode - 告警唯一编码
 * @property {string} id - 告警主键ID
 * @property {boolean} state - true:开启，false:屏蔽
 */

/**
 * @typedef {Object} AlarmConfig
 * @property {AlarmCodeList[]} alarmCodeList - 变动的告警编码集合
 * @property {string[]} stationIdList - 电站ID集合
 */

/**
 * @typedef {Object} AlarmQueryParams
 * @property {number} [act] - 告警动作
 * @property {string} [alarmCode] - 故障唯一码
 * @property {string} [code] - 故障编码
 * @property {string} [endTime] - 结束时间
 * @property {string} [pageNum] - 页码
 * @property {string} [pageSize] - 每页展示
 * @property {string} [startTime] - 开始时间
 * @property {number} [stationId] - 电站ID
 * @property {string} [uuid] - 设备uuid
 */


/**
 * 查询电站对应的告警编码集合
 * @param {number|string} stationId 电站ID
 * @returns 
 */
export function getStationAlarmCodeList(stationId) {
  return request({
    url: `/dashboard/v3/alarm/getStationAlarmCodeList/${stationId}`,
    method: 'get'
  })
}


/**
 * 修改电站对应的告警码开放权限
 * @param {AlarmConfig} alramConfig 
 * @returns 
 */
export function changeStationAlarmCode(alramConfig) {
  return request({
    url: `/dashboard/v3/alarm/changeStationAlarmCode`,
    method: 'post',
    data: alramConfig
  })
}


/**
 * 查询历史告警动作记录(展示给客户)
 * @param {AlarmQueryParams} params - 请求参数
 */
export function getAlarmHistory(params) {
  return request({
    url: '/dashboard/v3/alarm/getAlarmHistory',
    method: 'get',
    params
  })
}

/**
 * 获取告警快速智能分析
 * @param {Object} context 告警上下文
 * @param {String} context.source 来源标识 (alarm_detail)
 * @param {String} context.intent 意图标识 (alarm.analyze_summary)
 * @param {Object} context.context 具体上下文数据
 * @returns {Promise<Object>} 分析结果
 */
export function getAlarmQuickAnalysis(context) {
  return request({
    url: `/dashboard/v3/ops/agent/alarm/quick-analysis`,
    method: 'post',
    data: context
  })
}

/**
 * 获取告警看板概览数据
 * @param {Object} params - 请求参数
 * @param {number} params.stationId - 电站ID (必填)
 * @param {string} [params.date] - 查询日期，格式 yyyy-MM-dd，不传时后端默认当天
 * @returns {Promise<Object>} 概览数据
 */
export function getAlarmDashboardOverview(params) {
  return request({
    url: '/dashboard/v3/alarm/dashboard/overview',
    method: 'get',
    params
  })
}

/**
 * 获取告警看板分类告警列表
 * @param {Object} params - 请求参数
 * @param {string} [params.category] - 告警分类；不传默认ALL，可选：ALL、HIGH_PRIORITY、INCOME_IMPACT、REPEAT、UNRECOVERED
 * @param {string} [params.date] - 查询日期，格式 yyyy-MM-dd；不传时默认查询当天
 * @param {number} [params.pageNum] - 当前页码，从1开始；不传或小于1时默认第1页
 * @param {number} [params.pageSize] - 每页条数；不传或小于1时默认20条，最大100条
 * @param {number} params.stationId - 电站ID，必填，用于查询指定电站的当日告警
 * @returns {Promise<Object>} 分类告警列表
 */
export function getAlarmDashboardPriorityList(params) {
  return request({
    url: '/dashboard/v3/alarm/dashboard/priority-list',
    method: 'get',
    params
  })
}

/**
 * 获取告警详情
 * @param {number} alarmId - 告警记录ID
 * @param {Object} [params] - 请求参数
 * @param {number} [params.stationId] - 电站ID (可选，传入时后端会校验该告警是否属于该电站)
 * @returns {Promise<Object>} 告警详情
 */
export function getAlarmDashboardDetail(alarmId, params) {
  return request({
    url: `/dashboard/v3/alarm/dashboard/${alarmId}`,
    method: 'get',
    params
  })
}

/**
 * 获取告警分析上下文（图表聚合数据）
 * @param {Object} params - 请求参数
 * @param {string} params.startTime - 开始时间，格式 yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式 yyyy-MM-dd HH:mm:ss
 * @param {string|number} [params.stationId] - 电站ID，不传表示全部电站
 * @returns {Promise<Object>} 图表聚合数据上下文
 */
export function getAlarmAnalysisContext(params) {
  return request({
    url: '/dashboard/v3/alarm/analysisContext',
    method: 'get',
    params
  })
}

/**
 * 获取告警分析顶部统计卡片数据（告警总数/影响安全/影响收益/普通告警/涉及电站数）
 * @param {Object} params - 请求参数
 * @param {string} params.startTime - 开始时间，格式 yyyy-MM-dd HH:mm:ss
 * @param {string} params.endTime - 结束时间，格式 yyyy-MM-dd HH:mm:ss
 * @param {string|number} [params.stationId] - 电站ID，不传表示全部电站
 * @returns {Promise<Object>} 统计汇总数据
 */
export function getAlarmSummary(params) {
  return request({
    url: '/dashboard/v3/alarm/alarmSummary',
    method: 'get',
    params
  })
}

function getAlarmAnalysisData(path, params) {
  return request({
    url: `/dashboard/v3/alarm/${path}`,
    method: 'get',
    params
  })
}

export function getAlarmTrend(params) {
  return getAlarmAnalysisData('alarmTrend', params)
}

export function getAlarmHourDistribution(params) {
  return getAlarmAnalysisData('alarmHourDistribution', params)
}

export function getHighFrequencyAlarm(params) {
  return getAlarmAnalysisData('highFrequencyAlarm', params)
}

export function getDeviceAlarmDistribution(params) {
  return getAlarmAnalysisData('deviceAlarmDistribution', params)
}

export function getEblockModelAlarm(params) {
  return getAlarmAnalysisData('eblockModelAlarm', params)
}

export function getStationAlarmRank(params) {
  return getAlarmAnalysisData('stationAlarmRank', params)
}

export function getRegionAlarmAnalysis(params) {
  return getAlarmAnalysisData('regionAlarmAnalysis', params)
}

export function getStationDeviceAlarm(params) {
  return getAlarmAnalysisData('stationDeviceAlarm', params)
}

//#endregion
